import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import { useController } from "react-hook-form";
import { useTranslation } from "react-i18next";

import surveyLinkStyles from "../../styles/surveyLinkStyles";
import { SUBNETWORKS } from "../../subnetworks";

const SubnetworkOptions = ({ control, isWashington }) => {
  const { t } = useTranslation(["preChat"]);
  const theme = useTheme();
  const {
    field: { value, onChange },
  } = useController({ name: "subnetwork", control });

  const controlSx = (shape) => ({
    color: theme.palette.secondary.dark,
    "&.Mui-checked": {
      color: theme.palette.secondary.dark,
    },
    "&.Mui-focusVisible": {
      outline: `1px dashed ${theme.palette.secondary.dark}`,
      outlineOffset: "-5px",
    },
    borderRadius: shape === "square" ? "4px" : "50%",
    padding: "9px",
    margin: 0,
    alignSelf: "flex-start",
  });

  const optionRowSx = {
    alignItems: "flex-start",
    margin: 0,
    "&:hover .MuiButtonBase-root": {
      backgroundColor: theme.palette.secondary.light,
    },
  };

  const optionLabel = (title, description) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        paddingTop: "9px",
      }}
    >
      <Typography
        variant="h4"
        sx={{
          color: theme.palette.secondary.main,
          fontSize: "16px",
          fontWeight: 800,
          lineHeight: 1.4,
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: theme.palette.secondary.main,
          fontSize: "16px",
          lineHeight: 1.8,
        }}
      >
        {description}
      </Typography>
    </Box>
  );

  const linkStyles = surveyLinkStyles(theme);

  const clearSelectionButton = (
    <Button
      disableRipple
      onClick={() => onChange("")}
      sx={{
        ml: "42px",
        padding: 0,
        minWidth: 0,
        textTransform: "none",
        fontSize: "16px",
        fontWeight: 400,
        textDecoration: "underline",
        alignSelf: "flex-start",
        color: linkStyles.color,
        textDecorationColor: linkStyles.textDecorationColor,
        "&:hover": {
          ...linkStyles["&:hover"],
          textDecoration: "underline",
          backgroundColor: "transparent",
        },
        "&.Mui-focusVisible": {
          ...linkStyles[":focus"],
          backgroundColor: "transparent",
        },
      }}
    >
      {t("subnetworks.clearSelection")}
    </Button>
  );

  const radioOption = (subnetwork, title, description, sx) => (
    <Box sx={{ display: "flex", flexDirection: "column", ...sx }}>
      <FormControlLabel
        value={subnetwork}
        sx={optionRowSx}
        control={<Radio disableRipple tabIndex={0} sx={controlSx("circle")} />}
        label={optionLabel(title, description)}
      />
      {value === subnetwork && clearSelectionButton}
    </Box>
  );

  return (
    <Box sx={{ mt: 1, mb: 2 }}>
      <Typography
        variant="h4"
        sx={{
          color: theme.palette.primary.main,
          fontSize: "20px",
          fontWeight: 800,
          lineHeight: 1.4,
          mb: 1.5,
        }}
      >
        {t("subnetworks.heading")}
      </Typography>
      {isWashington ? (
        <RadioGroup
          value={value}
          onChange={(event) => onChange(event.target.value)}
        >
          {radioOption(
            SUBNETWORKS.LGBTQ,
            t("subnetworks.lgbtqTitle"),
            t("subnetworks.lgbtqDescription"),
            { mb: 1 },
          )}
          {radioOption(
            SUBNETWORKS.NATIVE,
            t("subnetworks.nativeTitle"),
            t("subnetworks.nativeDescription"),
          )}
        </RadioGroup>
      ) : (
        <FormControlLabel
          sx={optionRowSx}
          control={
            <Checkbox
              disableRipple
              checked={value === SUBNETWORKS.LGBTQ}
              onChange={(event) =>
                onChange(event.target.checked ? SUBNETWORKS.LGBTQ : "")
              }
              sx={controlSx("square")}
            />
          }
          label={optionLabel(
            t("subnetworks.lgbtqTitle"),
            t("subnetworks.lgbtqDescription"),
          )}
        />
      )}
    </Box>
  );
};

SubnetworkOptions.propTypes = {
  control: PropTypes.object.isRequired,
  isWashington: PropTypes.bool.isRequired,
};

export default SubnetworkOptions;
